import { TRACKING } from "@/lib/tracking-config";
import { readConsent } from "@/lib/consent";

/** Nomes de conversão usados no site. */
export type ConversionEvent = "Lead" | "Contact" | "InitiateCheckout" | "ViewContent";

/** GA4 usa snake_case; mapeamos a partir do nome Meta. */
const GA4_NAME: Record<ConversionEvent, string> = {
  Lead: "generate_lead",
  Contact: "contact",
  InitiateCheckout: "begin_form",
  ViewContent: "view_content",
};

export type UserData = {
  email?: string | null;
  phone?: string | null;
};

function newEventId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readFbCookie(name: string) {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : undefined;
}

/**
 * Dispara uma conversão em GA4, GTM (dataLayer), Meta Pixel (browser) e
 * Meta Conversions API (servidor), partilhando o mesmo event_id para
 * deduplicação entre browser e servidor.
 */
export function trackConversion(
  event: ConversionEvent,
  params: Record<string, unknown> = {},
  user: UserData = {},
) {
  if (typeof window === "undefined") return;
  const consent = readConsent();
  const analytics = consent?.analytics === true;
  const marketing = consent?.marketing === true;
  // Sem consentimento nada é enviado.
  if (!analytics && !marketing) return;
  const eventId = newEventId();

  try {
    if (analytics && TRACKING.ga4MeasurementId && window.gtag) {
      window.gtag("event", GA4_NAME[event], { ...params, event_id: eventId });
    }
    if (TRACKING.gtmContainerId && (analytics || marketing)) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: GA4_NAME[event], ...params, event_id: eventId });
    }
    if (marketing && TRACKING.metaPixelId && window.fbq) {
      window.fbq("track", event, params, { eventID: eventId });
    }
  } catch {
    /* tracking nunca deve quebrar a experiência do utilizador */
  }

  // Meta Conversions API (server-side) — só com consentimento de marketing.
  if (!marketing) return;
  void fetch("/api/public/meta-capi", {
    method: "POST",
    headers: { "content-type": "application/json" },
    keepalive: true,
    body: JSON.stringify({
      eventName: event,
      eventId,
      marketingConsent: true,
      consentVersion: consent?.version,
      eventSourceUrl: window.location.href,
      customData: params,
      userData: {
        email: user.email ?? undefined,
        phone: user.phone ?? undefined,
        fbp: readFbCookie("_fbp"),
        fbc: readFbCookie("_fbc"),
      },
    }),
  }).catch(() => undefined);
}
